import {
  Component,
  EventEmitter,
  OnInit,
  Renderer2,
  ChangeDetectionStrategy
} from '@angular/core';
import { Router } from '@angular/router';
import { AgGridModule } from 'ag-grid-angular';
import {
  GridOptions,
  GridReadyEvent,
  ICellRendererParams,
} from 'ag-grid-community';
import { OrganisationService } from 'src/app/organisation/organisation.service';
import { AgGridService } from 'src/app/shared/ag-grid/ag-grid.service';
import { SharedModule } from 'src/app/shared/shared.module';
import { CrewChangeServices } from '../crew-change.service';
import { DocumentService } from 'src/app/documents/documents.service';
import { UserLocalStoreService } from 'src/app/shared/services/user-local-store.service';
import { Crew, CrewChange } from 'src/app/generated/services/transportation/crew/crew';
import { CrewServiceSpa } from 'src/app/generated/services/transportation/crew/crewSpa';

@Component({
  selector: 'app-crew-appraisals-table',
  imports: [SharedModule, AgGridModule],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './crews-appraisals.component.html',
})
export class CrewsAppraisalsComponent implements OnInit {
  title: EventEmitter<string> = new EventEmitter();

  gridOptions!: GridOptions;
  gridRestored: boolean = false;
  _crews: Crew[] = [];
  appraisal: any;
  selectedCrew!:Crew;
  getCountryCode = this._organisationService.getCountryCode;

  constructor(
    private _crewChangeService: CrewChangeServices,
    private _organisationService: OrganisationService,
    private _renderer: Renderer2,
    private _documentsService: DocumentService,
    private _router: Router,
    private _userLocalStorage: UserLocalStoreService,
    private _crewService: CrewServiceSpa
  ) {}

  ngOnInit(): void {
    this.title.emit('Crew Appraisals');
    this.gridOptions = AgGridService.getOptions();
    this.getAllCrews();
  }

  columnDefs = [
    {
      headerName: 'Name',

      valueGetter: (params: ICellRendererParams) =>
        `${params.data?.firstName} ${params.data?.lastName}`,
      cellRenderer: (params: ICellRendererParams) => {
        const div = this._renderer.createElement('div');
        const countrySpan = this._renderer.createElement('span');
        const countryCode = this.getCountryCode(params.data.nationality);
        const profileImg = this._renderer.createElement('img');
        this._renderer.addClass(profileImg, 'active');
        this._renderer.addClass(profileImg, 'img-size');
        this._renderer.addClass(profileImg, 'ms-1');
        this._renderer.addClass(profileImg, 'rounded-circle');

        let src = '/assets/icons/main-logo/person.jpg';
        if (params.data.photoDocumentId) {
          this._documentsService
            .download(params.data.photoDocumentId)
            .subscribe((b) => {
              src = URL.createObjectURL(b.body!);
              this._renderer.setAttribute(profileImg, 'src', src);
            });
        }
        this._renderer.setAttribute(profileImg, 'src', src);

        const fullName = `${params.data?.firstName} ${params.data?.lastName}`;
        this._renderer.addClass(countrySpan, countryCode[0]);
        this._renderer.addClass(countrySpan, countryCode[1]);

        const text = AgGridService.editButtonWithText(
          this._renderer,
          this._router,
          this.edit,
          params.data,
          fullName,
        );

        const statusSpan = this._renderer.createElement('span');
        const status = this.getActivationStatus(params.data).split(' ');
        this._renderer.addClass(statusSpan, status[0]);
        this._renderer.addClass(statusSpan, status[1]);
        this._renderer.addClass(statusSpan, status[2]);
        this._renderer.addClass(statusSpan, status[3]);
        this._renderer.appendChild(div, countrySpan);
        this._renderer.appendChild(div, profileImg);
        this._renderer.appendChild(div, text);
        this._renderer.appendChild(div, statusSpan);

        return div;
      },

      filterValueGetter: (params: ICellRendererParams) => {
        const status = this._crewChangeService.getLatestStatus(params.data);
        return `${params.data?.firstName} ${params.data?.lastName} ${status}`;
      },
    },

    {
      headerName: 'Status',
      valueGetter: (params: ICellRendererParams) => this.getStatus(params.data),
    },

    {
      headerName: 'Last Rank',
      valueGetter: (params: ICellRendererParams) => {
        if (params.data?.crewChanges?.length > 0) {
          return this.getLastCrewChange(params.data.crewChanges)?.rank || '';
        }
        return '';
      },
    },

    {
      headerName: 'Appraisals',
      valueGetter: (params: ICellRendererParams) =>
        this.getMonthlyAppraisals(params.data.crewChanges),

      cellRenderer: (params: ICellRendererParams) => {
        const div = this._renderer.createElement('div');

        params.value?.forEach((m: any, index: number) => {
          const span = this._renderer.createElement('span');

          this._renderer.addClass(
            span,
            m.has ? 'text-success' : 'text-danger'
          );

          if (m.has) {
            this._renderer.setStyle(span, 'cursor', 'pointer');

            this._renderer.setAttribute(
              span,
              'data-bs-toggle',
              'modal'
            );

            this._renderer.setAttribute(
              span,
              'data-bs-target',
              '#appraisalModal'
            );

            this._renderer.listen(span, 'click', () => {
              this.showAppraisalPreview(m.appraisal, params.data);
            });
          }

          this._renderer.appendChild(
            span,
            this._renderer.createText(m.month.toString())
          );

          this._renderer.appendChild(div, span);

          if (index < params.value.length - 1) {
            this._renderer.appendChild(
              div,
              this._renderer.createText(', ')
            );
          }
        });

        return div;
      },
    }
  ];

  edit(router: Router, crew: Crew) {
    router.navigate(['/vessels/crew-change/crew/profile/', crew.id]).then();
  }

  gridSetting(event: GridReadyEvent) {
    AgGridService.setting(event);
  }

  saveGridState(event: GridReadyEvent) {
    AgGridService.saveGridState(
      event,
      this.gridRestored,
      this._userLocalStorage,
      'crewGrid',
    );
  }

  restoreGridState(event: GridReadyEvent) {
    this.gridRestored = AgGridService.restoreGridState(
      event,
      this._userLocalStorage,
      'crewGrid',
    );
  }

  getStatus(crew: Crew) {
    if (crew) {
      return this._crewChangeService.getCrewStatus(crew);
    }
  }

  getLastCrewChange(crewChanges: CrewChange[]) {
    if (crewChanges.length > 0) {
      return this._crewChangeService.getLastCrewChange(crewChanges);
    } else {
      return null;
    }
  }

  getActivationStatus(crew: Crew) {
    return this._crewChangeService.getCrewActivationStatus(crew);
  }

  getAllCrews() {
    this._crewService.getCrew({}).then((crews) => {
      this._crews = crews.crews.map((c) => c.data).filter((c) => c !== undefined);

      this._crews.forEach((crew) => {
        crew.crewChanges.sort(
          (a, b) =>
            Math.max(...b.item.map((i) => i.time)) -
            Math.max(...a.item.map((i) => i.time)),
        );
      });
    });
  }

  getMonthlyAppraisals(
    crewChanges: CrewChange[],
  ): {
      month: number;
      has: boolean;
      appraisal: any
    }[] {
    const result: {
        month: number;
        has: boolean;
        appraisal: any
      }[] = [];

    const today = new Date();

    for (let i = 0; i <= 6; i++) {
      const date = new Date(
        today.getFullYear(),
        today.getMonth() - i,
        1
      );

      const monthNumber = date.getMonth() + 1;

      const appraisal = crewChanges
        ?.flatMap(c => c.appraisals || [])
        .find(a => {
          const ad = new Date(a.date);

          return (
            ad.getMonth() === date.getMonth() &&
          ad.getFullYear() === date.getFullYear()
          );
        });

      result.push({
        month: monthNumber,
        has: !!appraisal,
        appraisal: appraisal || null,
      });
    }
    return result.reverse();
  }

  showAppraisalPreview(appraisal: any, crew: Crew) {
    this.appraisal = appraisal;
    this.selectedCrew = crew;
  }
}
